import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Input from '../../components/form/Input';
import { loginSchema } from '../../Helpers/Yup';
import { loginAdmin, clearError } from '../../redux/slices/adminSlice';
import '../website/website.css';

export default function AdminLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { adminLoading, error, isAuthenticated, passwordUpdateRequired } = useSelector((state) => state.admin);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    } else if (passwordUpdateRequired) {
      // Redirect to password update screen if required
      navigate('/admin/update-password');
    }
    return () => {
      dispatch(clearError());
    };
  }, [isAuthenticated, passwordUpdateRequired, navigate, dispatch]);

  const onSubmit = async (data) => {
    dispatch(loginAdmin(data));
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image/Branding */}
      <div className="hidden lg:flex lg:w-1/2 main-bg items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="mb-8">
            <div className="text-4xl font-bold text-white mb-4">
              xy<span className="bg-gradient-to-r from-yellow-300 to-white bg-clip-text text-transparent">leads</span>
            </div>
            <p className="text-xl text-white text-opacity-90 leading-relaxed">
              Advanced call management for administrators
            </p>
          </div>
          
          {/* Sample illustration */}
          <div className="glass-card-white p-8 rounded-2xl">
            <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
              <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h3 className="text-lg font-semibold gradient-text mb-2">Admin Dashboard</h3>
            <p className="text-gray-600 text-sm">
              Manage teams, view comprehensive reports, and oversee call operations.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4 text-center">
            <div className="text-white text-opacity-80">
              <div className="text-2xl font-bold">500+</div>
              <div className="text-sm">Users</div>
            </div>
            <div className="text-white text-opacity-80">
              <div className="text-2xl font-bold">50+</div>
              <div className="text-sm">Reports</div>
            </div>
            <div className="text-white text-opacity-80">
              <div className="text-2xl font-bold">24/7</div>
              <div className="text-sm">Monitoring</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden text-center">
            <div className="text-3xl font-bold gradient-text mb-2">
              xy<span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">leads</span>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Admin Portal</h2>
            <p className="text-gray-600">Access your admin dashboard</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-red-800 text-sm">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  {...register('email')}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  {...register('password')}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-purple-600 hover:text-purple-500 transition-colors">
                  Forgot password?
                </a>
              </div>
            </div>

            <button
              type="submit"
              disabled={adminLoading}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 transition-all"
            >
              {adminLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="text-center">
            <Link 
              to="/" 
              className="text-gray-500 hover:text-purple-600 transition-colors text-sm"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}